'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Compass, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, Layout, MapPin } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function CelticCrossPage() {
  const cardPositions = [
    { position: 1, name: "Şu An", description: "Mevcut durum, şimdiki zaman", placement: "center" },
    { position: 2, name: "Meydan Okuma", description: "Karşılaştığınız zorluk veya etki", placement: "center-cross" },
    { position: 3, name: "Uzak Geçmiş", description: "Köken, temel, geçmişteki etkenler", placement: "bottom" },
    { position: 4, name: "Olası Gelecek", description: "Mevcut yolda devam ederse olabilecekler", placement: "top" },
    { position: 5, name: "Yakın Geçmiş", description: "Son zamanlarda yaşananlar", placement: "left" },
    { position: 6, name: "Yakın Gelecek", description: "Önümüzdeki dönemde olacaklar", placement: "right" },
    { position: 7, name: "Yaklaşımınız", description: "Bu duruma karşı tavrınız", placement: "staff-bottom" },
    { position: 8, name: "Dış Etkiler", description: "Çevrenizin, diğer insanların etkisi", placement: "staff-2" },
    { position: 9, name: "İç Dünya", description: "Umutlarınız, korkularınız, düşünceleriniz", placement: "staff-3" },
    { position: 10, name: "Sonuç", description: "Tüm faktörleri değerlendirdiğinde olası sonuç", placement: "staff-top" }
  ];

  const spreadSteps = [
    {
      step: 1,
      title: "Merkez Haç (1-6)",
      description: "İlk altı kartı haç şeklinde yerleştirin",
      details: "Durumun çekirdeğini oluşturan temel kartlar"
    },
    {
      step: 2,
      title: "Sağ Sütun (7-10)",
      description: "Dört kartı sağda dikey olarak dizin",
      details: "Durumun gelişimini ve sonucunu gösteren kartlar"
    },
    {
      step: 3,
      title: "Bütünsel Yorumlama",
      description: "Tüm kartları birlikte değerlendirin",
      details: "Kartlar arası bağlantıları ve genel mesajı analiz edin"
    }
  ];

  const interpretationTips = [
    {
      title: "Hikaye Akışı",
      tip: "3-1-6 kartlarını zaman çizgisinde okuyun (geçmiş-şimdi-gelecek)",
      icon: <Clock className="h-5 w-5" />
    },
    {
      title: "İç-Dış Denge",
      tip: "7-9 kartları iç dünyayı, 8-10 kartları dış dünyayı temsil eder",
      icon: <Brain className="h-5 w-5" />
    },
    {
      title: "Zorluk Analizi",
      tip: "2. kart genellikle aşılması gereken en büyük engeli gösterir",
      icon: <Target className="h-5 w-5" />
    },
    {
      title: "Sonuç Değerlendirmesi",
      tip: "10. kart kesin değil, mevcut duruma göre en olası sonuçtur",
      icon: <Eye className="h-5 w-5" />
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Celtic Cross</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Compass className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Celtic Cross (Kelt Haçı)</h1>
              <p className="text-yellow-300 font-medium">Orta Seviye • 10 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            En ünlü ve kapsamlı tarot spread'i. Durumun tüm yönlerini detaylı bir şekilde inceler.
            Karmaşık yaşam durumları için ideal olan bu yöntem, geçmişten geleceğe kadar her şeyi ele alır.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">20-45 dakika</p>
            <p className="text-slate-400 text-sm">Detaylı analiz</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Kapsamlı analiz</p>
            <p className="text-slate-400 text-sm">Çok boyutlu değerlendirme</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-yellow-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">Orta</p>
            <p className="text-slate-400 text-sm">Deneyim gerektirir</p>
          </div>
        </div>

        {/* Celtic Cross Dizilimi */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Celtic Cross Dizilimi</h2>
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
            <div className="flex justify-center">
              <div className="grid grid-cols-6 gap-4 max-w-2xl">
                {/* Row 1 */}
                <div className="col-start-3"></div>
                <div className="bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  4<br/><span className="text-xs">Olası<br/>Gelecek</span>
                </div>
                <div className="col-start-6 bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  10<br/><span className="text-xs">Sonuç</span>
                </div>

                {/* Row 2 */}
                <div className="bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  5<br/><span className="text-xs">Yakın<br/>Geçmiş</span>
                </div>
                <div className="relative bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  1<br/><span className="text-xs">Şu An</span>
                  <div className="absolute inset-0 bg-yellow-700 rounded-lg transform rotate-45 -z-10 flex items-center justify-center">
                    <span className="text-xs transform -rotate-45">2</span>
                  </div>
                </div>
                <div className="bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  6<br/><span className="text-xs">Yakın<br/>Gelecek</span>
                </div>
                <div className="bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  9<br/><span className="text-xs">İç<br/>Dünya</span>
                </div>

                {/* Row 3 */}
                <div className="col-start-3"></div>
                <div className="bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  3<br/><span className="text-xs">Uzak<br/>Geçmiş</span>
                </div>
                <div className="col-start-6 bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  8<br/><span className="text-xs">Dış<br/>Etkiler</span>
                </div>

                {/* Row 4 */}
                <div className="col-start-6 bg-yellow-600 rounded-lg p-3 text-center text-white font-bold flex flex-col items-center justify-center h-20">
                  7<br/><span className="text-xs">Yaklaşım</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kart Pozisyonları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kart Pozisyonları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cardPositions.map((card) => (
              <div key={card.position} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{card.position}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{card.name}</h3>
                </div>
                <p className="text-slate-300 text-sm">{card.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Adım Adım Okuma */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Adım Adım Okuma Süreci</h2>
          <div className="space-y-6">
            {spreadSteps.map((step) => (
              <div key={step.step} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">{step.step}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-2">{step.title}</h3>
                    <p className="text-slate-300 mb-2">{step.description}</p>
                    <p className="text-slate-400 text-sm italic">{step.details}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yorumlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationTips.map((tip, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                    {tip.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-100">{tip.title}</h3>
                </div>
                <p className="text-slate-300">{tip.tip}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Yaygın Zorluklar */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaygın Zorluklar ve Çözümler</h2>
          <div className="space-y-4">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">🤯 Çok Karmaşık Geliyor</h3>
              <p className="text-slate-300 mb-2">
                <strong>Çözüm:</strong> İlk önce merkez haçı (1-6) yorumlayın, sonra sağ sütunu ekleyin.
                Her seferde 2-3 kart üzerinde odaklanın.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">🔄 Kartlar Çelişiyor</h3>
              <p className="text-slate-300 mb-2">
                <strong>Çözüm:</strong> Çelişki durumun karmaşıklığını yansıtabilir. Farklı açılardan
                bakmanız gerektiğini düşünün. Her kartın kendi pozisyonundaki rolünü göz önünde bulundurun.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">😰 Olumsuz Kartlar Çıkıyor</h3>
              <p className="text-slate-300 mb-2">
                <strong>Çözüm:</strong> Olumsuz kartlar genellikle farkındalık yaratır. Bu kartların
                hangi pozisyonda olduğu önemli - geçmişte ise aştığınız, gelecekte ise hazırlanabileceğiniz şeylerdir.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">⏰ Çok Zaman Alıyor</h3>
              <p className="text-slate-300 mb-2">
                <strong>Çözüm:</strong> Celtic Cross derin okuma yöntemidir. Acele etmeyin,
                önemli sorular için ayırın. Günlük sorular için tek kart veya üç kart yayılımı kullanın.
              </p>
            </div>
          </div>
        </div>

        {/* Ne Zaman Kullanılır */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ne Zaman Kullanılır?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-900/20 border border-green-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-200 mb-4">İdeal Durumlar</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Önemli yaşam kararları</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Karmaşık durumlar</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Uzun vadeli planlama</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Kişisel gelişim</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Yıllık değerlendirme</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-4">Kaçınılması Gereken Durumlar</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Günlük basit sorular</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Acil karar gereken durumlar</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Çok yorgun olduğunuzda</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Tarot'a yeni başladığınızda</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/uc-kart"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Üç Kart
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/iliski-yayilimi"
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
          >
            Sonraki: İlişki Yayılımı
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}